import { RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { AppProviders } from "@/app/providers";
import { createQueryClient } from "@/app/queryClient";
import { createAppRouter } from "@/app/router";

import "./styles.css";

// Inside the Android/iOS app the native bridge is there before any script runs; the
// phone-only code (src/native) loads only then, so the website never downloads it.
const inApp = !!(window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.();
const native = inApp ? await import("@/native") : null;
await native?.initNative();
// the website reports its build for Admin -> Users (the app does it in src/native/version.ts)
if (!inApp) (await import("@/lib/appVersion")).startVersionReport();

const queryClient = createQueryClient();
const router = createAppRouter(queryClient);
// a tapped push notification opens its page (src/native/push.ts)
native?.setNavigator((route) => router.history.push(route));

const root = document.getElementById("root");
if (!root) throw new Error("#root missing");

function mount(el: HTMLElement) {
  createRoot(el).render(
    <StrictMode>
      <AppProviders queryClient={queryClient}>
        <RouterProvider router={router} />
      </AppProviders>
    </StrictMode>,
  );
}

/**
 * Resolves once the head's stylesheets have loaded. The first render measures
 * the layout (the ticker's rows), and a layout read before the CSS has arrived
 * makes Firefox paint the page unstyled. In dev the CSS comes as <style> tags.
 */
function stylesReady(): Promise<unknown> {
  const pending = [...document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')].filter((l) => !l.sheet);
  return Promise.all(
    pending.map(
      (l) =>
        new Promise<void>((done) => {
          l.addEventListener("load", () => done(), { once: true });
          l.addEventListener("error", () => done(), { once: true });
        }),
    ),
  );
}

// A public page's HTML comes with the page prerendered inside #root (vite.brand.ts).
// React's first commit replaces it, so load the route (session check, lazy page
// chunk) first: the first commit is then the same page, not an empty pending
// state, and nothing flashes. Other pages mount as soon as the CSS is there.
const routeReady = root.firstElementChild ? router.load().catch(() => undefined) : undefined;
// Never wait more than 3 s on a stylesheet that hangs without load or error.
const stylesOrTimeout = Promise.race([stylesReady(), new Promise((done) => setTimeout(done, 3000))]);
void Promise.all([routeReady, stylesOrTimeout]).then(() => {
  mount(root);
  native?.nativeReady();
});
